import { useState, useEffect } from "react";
import { Box, Typography, Checkbox, CircularProgress } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./heathOrganisation.module.css";
import {
  useGetHealthOrganisationDetailsQuery,
  useUpdateHealthOrganisationDetailsMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { colors } from "../../theme/colors";

const HealthOrganisationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [faxNumberSelected, setFaxNumberSelected] = useState(false);
  const [emailNumberSelected, setEmailNumberSelected] = useState(false);
  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    data: healthOrganisationDetails,
    isLoading,
    isFetching,
  } = useGetHealthOrganisationDetailsQuery(id ?? "");
  const [updateHealthOrganisationDetails] =
    useUpdateHealthOrganisationDetailsMutation();

  useEffect(() => {
    setFaxNumberSelected(
      healthOrganisationDetails?.address?.is_fax_delivery ?? false
    );
    setEmailNumberSelected(
      healthOrganisationDetails?.address?.is_mail_delivery ?? false
    );
  }, [healthOrganisationDetails]);

  const handleActivate = async () => {
    if (!healthOrganisationDetails?.id) {
      return;
    }
    try {
      setIsActivating(true);
      await updateHealthOrganisationDetails({
        is_active: true,
        id: healthOrganisationDetails.id,
        deactivate_reason: "",
      }).unwrap();
      setActivateModalVisible(false);
    } catch (error: any) {
      setActivateModalVisible(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading || isFetching ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "15px 40px" }}>
            <Typography className={style.healthOrgDetailHeading}>
              Health Organisation Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Name</Typography>
                <Typography className={style.value}>
                  {healthOrganisationDetails?.name}
                </Typography>
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Provider Number</Typography>
                <Typography className={style.value}>
                  {healthOrganisationDetails?.address?.provider_number}
                </Typography>
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Address</Typography>
                <Typography className={style.value}>
                  {`${
                    healthOrganisationDetails?.address?.unit_number
                      ? healthOrganisationDetails?.address?.unit_number + " "
                      : ""
                  }${healthOrganisationDetails?.address?.street_number ?? ""} ${
                    healthOrganisationDetails?.address?.street_name
                  }, ${healthOrganisationDetails?.address?.suburb ?? ""} ${
                    healthOrganisationDetails?.address?.state ?? ""
                  } ${healthOrganisationDetails?.address?.postcode ?? ""}`}
                </Typography>
              </Box>
            </Box>
            <Typography className={style.healthOrgDetailHeading}>
              Contact Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>
                  General Enquiries
                </Typography>
                <Typography className={style.value}>
                  {healthOrganisationDetails?.account_manager_mail}
                </Typography>
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>
                  Additional Contacts
                </Typography>
                <Typography className={style.value} sx={{ display: "flex" }}>
                  {"For department-specific contacts, please refer to the"}
                  <Typography
                    component="span"
                    onClick={() => {
                      navigate("/users", {
                        state: { selectedTab: "hospital" },
                      });
                    }}
                    color="primary.main"
                    display={"block"}
                    sx={{
                      textDecoration: "none",
                      paddingX: "5px",
                      cursor: "pointer",
                    }}
                  >
                    'User List'
                  </Typography>
                  {"section."}
                </Typography>
              </Box>
            </Box>
            <Typography className={style.healthOrgDetailHeading}>
              Request Form Delivery Details (Medical Records)
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Fax Number</Typography>
                <Typography className={style.value}>
                  {healthOrganisationDetails?.address?.fax}
                </Typography>
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Email</Typography>
                <Typography className={style.value}>
                  {healthOrganisationDetails?.address?.mail}
                </Typography>
              </Box>
            </Box>
            <Typography className={style.healthOrgDetailHeading}>
              Mode of Delivery
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Fax Number</Typography>
                <Checkbox
                  checked={faxNumberSelected}
                  disableTouchRipple
                  sx={{
                    transform: "scale(0.92)",
                    padding: "0px",
                    color: colors.primary,
                  }}
                  disabled
                />
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Email</Typography>
                <Checkbox
                  checked={emailNumberSelected}
                  disableTouchRipple
                  sx={{
                    transform: "scale(0.92)",
                    padding: "0px",
                  }}
                  disabled
                />
              </Box>
            </Box>
            {healthOrganisationDetails?.deactivate_reason && (
              <Box>
                <Typography className={style.healthOrgDetailHeading}>
                  Deactivation Details
                </Typography>
                <Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Reason for deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {healthOrganisationDetails?.deactivate_reason}
                    </Typography>
                  </Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Date of Deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {healthOrganisationDetails?.deactivated_at
                        ? dayjs(
                            healthOrganisationDetails?.deactivated_at
                          ).format("MMMM DD YYYY, hh:mm:ss A")
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: "transparent" },
                }}
                variant="outlined"
                onClick={() => navigate(-1)}
                label={"Close"}
              />
              {healthOrganisationDetails?.is_active ? (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Edit"}
                  onClick={() =>
                    navigate(
                      `/health-organisations/edit/${healthOrganisationDetails?.id}`
                    )
                  }
                />
              ) : (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Activate"}
                  onClick={() => setActivateModalVisible(true)}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
      {activateModalVisible && (
        <ConfirmationDialog
          visible={activateModalVisible}
          message={`Are you sure you want to activate "${healthOrganisationDetails?.name}"?`}
          onClose={() => setActivateModalVisible(false)}
          onConfirm={handleActivate}
          loading={isActivating}
        />
      )}
    </Box>
  );
};

export default HealthOrganisationDetails;
